import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getApiUrl, isApiUrlValid, logApiConfiguration } from '@/utils/apiUrl';
import { Capacitor } from '@capacitor/core';

// Configurazione base API - usa utility per gestire correttamente mobile
const API_BASE_URL = getApiUrl();

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    // Log configurazione API durante l'inizializzazione
    if (import.meta.env.DEV) {
      logApiConfiguration();
    }

    // Verifica che l'URL API sia valido per la piattaforma
    if (!isApiUrlValid()) {
      console.error('❌ URL API non valido per piattaforma mobile!');
      console.error('Configura VITE_API_URL nel file .env con un indirizzo IP valido');
    }

    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - Aggiungi JWT token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = this.getToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        console.log(`📤 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('❌ Request error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - Gestisci errori e refresh token
    this.client.interceptors.response.use(
      (response) => {
        console.log(`✅ API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config;

        console.error(`❌ API Error: ${error.response?.status} ${error.config?.url}`);

        // Token scaduto - redirect a login
        if (error.response?.status === 401 && originalRequest) {
          this.clearToken();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Forbidden
        if (error.response?.status === 403) {
          console.error('🚫 Accesso negato');
        }

        // Server error
        if (error.response?.status && error.response.status >= 500) {
          console.error('🔥 Errore del server');
        }

        return Promise.reject(error);
      }
    );
  }

  // Token management
  // Nota: Su mobile, localStorage funziona ma Capacitor Preferences è più sicuro
  // TODO: Considerare migrazione a Capacitor Preferences per storage sicuro
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  public setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  public clearToken(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  // Utility per verificare la connessione di rete (utile su mobile)
  public isOnline(): boolean {
    return navigator.onLine;
  }

  // Get base URL (utile per debug)
  public getBaseUrl(): string {
    return API_BASE_URL;
  }

  // Get platform info (utile per debug)
  public getPlatformInfo(): { platform: string; isNative: boolean } {
    return {
      platform: Capacitor.getPlatform(),
      isNative: Capacitor.isNativePlatform(),
    };
  }

  // HTTP Methods
  public async get<T>(url: string, config = {}) {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: any, config = {}) {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: any, config = {}) {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config = {}) {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: any, config = {}) {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
